import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, Observable } from 'rxjs';
import { AdditionalDocumentRequestDetailsDto, AdditionalDocumentReviewResultDto, AdditionalDocumentSubmissionDto } from '../../../_core/_model/additional-documents';
import { ApiResponse } from '../../../_core/_model/api-response';
import { AdditionalDocumentsService } from '../../../_core/_services/additional-documents.service';
import { NotificationService } from '../../../_core/_services/notification.service';

@Component({
  selector: 'app-admin-additional-document-details',
  standalone: false,
  templateUrl: './admin-additional-document-details.html',
  styleUrl: './admin-additional-document-details.css'
})
export class AdminAdditionalDocumentDetails implements OnInit {
  requestId = '';
  request: AdditionalDocumentRequestDetailsDto | null = null;
  loading = false;
  actionLoading = false;
  downloadingId: string | null = null;
  error: string | null = null;
  reviewNotes = '';
  rejectionReason = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private documents: AdditionalDocumentsService,
    private notify: NotificationService
  ) {}

  ngOnInit(): void {
    this.requestId = this.route.snapshot.paramMap.get('requestId') || '';
    if (!this.requestId) {
      this.router.navigate([this.basePath]);
      return;
    }
    this.load();
  }

  get headerPage(): string {
    return this.router.url.startsWith('/review/') ? 'Document Reviews' : 'Admin';
  }

  get basePath(): string {
    return this.router.url.startsWith('/review/')
      ? '/review/additional-documents'
      : '/admin/additional-documents';
  }

  load(): void {
    this.loading = true;
    this.error = null;
    this.documents.getAdminRequest(this.requestId)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: response => {
          this.request = response.data || null;
          this.reviewNotes = this.request?.reviewNotes || '';
        },
        error: error => {
          this.error = error?.error?.message || 'Unable to load this document request.';
          this.notify.showError(this.error!, 'Additional Documents');
        }
      });
  }

  startReview(): void {
    if (!this.request || this.actionLoading) return;
    this.runAction(
      this.documents.startReview(this.request.id, this.reviewNotes.trim() || null),
      'Document review started.'
    );
  }

  approve(): void {
    if (!this.request || this.actionLoading) return;
    const warning = this.request.scopeType === 'Deposit'
      ? 'Approve this document? If payment verification is present, this will credit and complete the held deposit.'
      : 'Approve this document request?';
    if (!window.confirm(warning)) return;

    this.runAction(
      this.documents.approve(this.request.id, this.reviewNotes.trim() || null),
      'Document approved.'
    );
  }

  reject(): void {
    if (!this.request || this.actionLoading) return;
    const reason = this.rejectionReason.trim();
    if (!reason) {
      this.notify.showError('A clear rejection reason is required so the user knows what replacement to upload.', 'Additional Documents');
      return;
    }
    if (!window.confirm('Reject this document and ask the user for a replacement upload?')) return;

    this.runAction(
      this.documents.reject(this.request.id, reason),
      'Document rejected. The request is open for a replacement upload.',
      true
    );
  }

  download(submission: AdditionalDocumentSubmissionDto): void {
    if (this.downloadingId) return;
    this.downloadingId = submission.id;
    this.documents.getAdminDownloadUrl(this.requestId, submission.id)
      .pipe(finalize(() => (this.downloadingId = null)))
      .subscribe({
        next: response => {
          if (response.data?.downloadUrl) window.open(response.data.downloadUrl, '_blank', 'noopener');
        },
        error: error => this.notify.showError(error?.error?.message || 'Unable to open the document.', 'Download Failed')
      });
  }

  canStartReview(): boolean {
    return this.request?.status === 'Submitted';
  }

  canDecide(): boolean {
    return !!this.request && ['Submitted', 'UnderReview'].includes(this.request.status);
  }

  statusClass(status: string): string {
    switch (status) {
      case 'Approved': return 'text-bg-success';
      case 'Requested':
      case 'AwaitingReplacement':
      case 'Rejected': return 'text-bg-danger';
      case 'Submitted':
      case 'UnderReview': return 'text-bg-warning';
      default: return 'text-bg-secondary';
    }
  }

  fileSize(bytes?: number | null): string {
    if (!bytes) return '—';
    if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private runAction(
    observable: Observable<ApiResponse<AdditionalDocumentReviewResultDto>>,
    successMessage: string,
    clearRejection = false
  ): void {
    this.actionLoading = true;
    observable.pipe(finalize(() => (this.actionLoading = false))).subscribe({
      next: response => {
        if (clearRejection) this.rejectionReason = '';
        this.notify.showSuccess(response?.message || successMessage, 'Additional Documents');
        this.load();
      },
      error: error => this.notify.showError(
        error?.error?.error?.details || error?.error?.message || 'The review action failed.',
        'Additional Documents'
      )
    });
  }
}
